import {
  ChangeEvent,
  MouseEvent,
  useRef,
  useState,
  type RefObject,
} from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Checkmark, EventSchedule } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications-api';
import { useQueryClient } from '@mint-lib/query-client';
import { Button, InputWithIcon } from '@mint-lib/tw-components';
import { Chat, Schedule } from '@mint-sdk/api-ai-assistant';

import { SidebarItem, SidebarItemProps } from '@myn/mui';

import { useOutsideClick } from '../../hooks/useOutsideClick.jsx';
import ChatItemEndAdornment from './ChatItemEndAdornment.jsx';

type Props = {
  chat: Chat.ChatItemType;
  onClick: (id: string) => void;
  isDisabled?: boolean;
  isMobile?: boolean;
  isAgentic?: boolean;
  onDeleteChat: () => void;
  onChangeChatName?: (newChatName: string) => void;
} & Pick<SidebarItemProps, 'variant' | 'selected'>;

const ChatItem = ({
  chat,
  selected,
  isMobile,
  isDisabled = false,
  isAgentic = false,
  onDeleteChat,
  onChangeChatName,
  onClick,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const childRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();
  const { mutateAsync: updateMutation, variables } = Chat.updateChatTitle();
  const { mutate: deleteMutation } = Chat.deleteChat();

  const [isHovered, setIsHovered] = useState(false);
  const [isEditChatNameMode, setIsEditChatNameMode] = useState(false);
  const [newChatName, setNewChatName] = useState(chat.chatTitle ?? 'New chat');
  const [isOverflowed, setIsOverflowed] = useState(false);

  const tooltipOpen = isHovered && isOverflowed && (!isMobile || !selected);

  const handleDeleteChat = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    deleteMutation({ urlParams: { chatSessionId: chat.chatSessionId } });

    setIsHovered(false);

    if (selected) {
      onDeleteChat();
    }
  };

  const handleOutsideClick = () => {
    setIsEditChatNameMode(false);
    setNewChatName(chat.chatTitle ?? 'New chat');
  };

  const ref = useOutsideClick(handleOutsideClick);

  const handleClickEditChatName = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsEditChatNameMode(true);
    setIsHovered(false);
  };

  const handleUpdateChatName = async () => {
    if (newChatName.length > 200) {
      enqueueSnackbar({
        subTitle: t(
          "Chat title hasn't been updated because exceeds the allowed limit of 200 characters",
        ),
        title: t('Error'),
        variant: 'error',
      });

      return;
    }

    if (newChatName.length) {
      const { chatSessionId } = chat;

      await updateMutation({
        urlParams: { chatSessionId },
        data: { chatSessionId, chatTitle: newChatName },
      });

      onChangeChatName?.(newChatName);
    }

    if (chat.type === 'scheduled') {
      const scheduledQueryKey = Schedule.getScheduleOptions({}).queryKey;

      queryClient.invalidateQueries({
        queryKey: scheduledQueryKey,
      });
    }

    setIsEditChatNameMode(false);
  };

  const onChatClick = (e: ChangeEvent<HTMLButtonElement>) => {
    const isActionBtnClicked = childRef.current?.contains(e.target);

    if (isActionBtnClicked) {
      return;
    }

    onClick(chat.chatSessionId);
  };

  const onHoverChatItem = ({
    currentTarget,
  }: {
    currentTarget: HTMLElement;
  }) => {
    const chat = currentTarget.firstChild as HTMLElement;
    setIsOverflowed(chat.scrollWidth > chat.offsetWidth);
    setIsHovered(true);
  };

  const onMouseLeaveItem = () => {
    setIsHovered(false);
  };

  const handleHideActionBtn = () => {
    setIsHovered(false);
  };

  if (isEditChatNameMode) {
    return (
      <div className="mt-2 w-full" ref={ref as RefObject<HTMLDivElement>}>
        <InputWithIcon
          autoFocus
          size="small"
          value={newChatName}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleUpdateChatName();
            }
          }}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setNewChatName(e.target.value)
          }
          icon={
            <Button
              icon
              variant="plain"
              size="small"
              aria-label="Update chat name"
              onClick={handleUpdateChatName}
            >
              <Checkmark />
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <SidebarItem
      text={variables?.data.chatTitle ?? chat.chatTitle ?? 'New chat'}
      onItemClick={onChatClick}
      variant={selected ? 'primary' : 'secondary'}
      selected={selected}
      tooltipOpen={tooltipOpen}
      onMouseEnter={onHoverChatItem}
      onMouseLeave={onMouseLeaveItem}
      disabled={isDisabled}
      sx={{
        width: '100% !important',
        height: isAgentic || !isMobile ? '32px' : '40px',
        paddingLeft: 2,
      }}
      startAdornment={
        chat.type === 'scheduled' ? (
          <EventSchedule className="fill-icon-primary" />
        ) : null
      }
      endAdornment={
        <ChatItemEndAdornment
          status={chat.status}
          isHovered={isHovered}
          isMobile={isMobile}
          selected={selected}
          childRef={childRef}
          onHideActionIcon={handleHideActionBtn}
          onDeleteChat={handleDeleteChat}
          onClickUpdateChatName={handleClickEditChatName}
        />
      }
    />
  );
};

export default ChatItem;
